/**
 * WatchStatusContext
 * ------------------
 * Global lightweight store for watched / loved / saved state.
 * Fetches once on login, exposes toggle functions that optimistically
 * update local sets + call the backend.
 */
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api } from "./api";

const WatchStatusContext = createContext(null);

export function WatchStatusProvider({ children, user }) {
  const [watchedIds, setWatchedIds] = useState(new Set());   // "mt:tmdb_id"
  const [lovedIds, setLovedIds]     = useState(new Set());
  const [savedIds, setSavedIds]     = useState(new Set());
  const [lists, setLists]           = useState([]);
  const loadedRef = useRef(false);

  const load = useCallback(async () => {
    if (!user) {
      setWatchedIds(new Set());
      setLovedIds(new Set());
      setSavedIds(new Set());
      setLists([]);
      loadedRef.current = false;
      return;
    }
    try {
      const [wlRes, listsRes] = await Promise.all([
        api.get("/watchlist"),
        api.get("/watchlist/lists"),
      ]);
      const items = wlRes.data?.items || [];
      const w = new Set(), l = new Set(), s = new Set();
      items.forEach(item => {
        const key = `${item.media_type}:${item.tmdb_id}`;
        s.add(key);
        if (item.watched_at) w.add(key);
        if (item.loved_at)   l.add(key);
      });
      setWatchedIds(w);
      setLovedIds(l);
      setSavedIds(s);
      setLists(listsRes.data?.lists || []);
      loadedRef.current = true;
    } catch {
      /* silent — user may be logged out */
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const key = (mt, id) => `${mt}:${id}`;

  const isWatched = (mt, id) => watchedIds.has(key(mt, id));
  const isLoved   = (mt, id) => lovedIds.has(key(mt, id));
  const isSaved   = (mt, id) => savedIds.has(key(mt, id));

  const toggleWatched = useCallback(async (mt, id, item = {}) => {
    const k = key(mt, id);
    const nowWatched = !watchedIds.has(k);
    setWatchedIds(prev => {
      const next = new Set(prev);
      nowWatched ? next.add(k) : next.delete(k);
      return next;
    });
    try {
      await api.post(`/watchlist/${mt}/${id}/watched`, {
        title: item.title || item.name || "",
        poster_path: item.poster_path || null,
      });
      // Signal home rails to remove watched title
      if (nowWatched) {
        window.dispatchEvent(new CustomEvent("fm-card-watched", {
          detail: { media_type: mt, tmdb_id: id }
        }));
      }
    } catch {
      // revert on error
      setWatchedIds(prev => {
        const next = new Set(prev);
        nowWatched ? next.delete(k) : next.add(k);
        return next;
      });
    }
  }, [watchedIds]);

  const toggleLoved = useCallback(async (mt, id, item = {}) => {
    const k = key(mt, id);
    const nowLoved = !lovedIds.has(k);
    setLovedIds(prev => {
      const next = new Set(prev);
      nowLoved ? next.add(k) : next.delete(k);
      return next;
    });
    if (nowLoved && !savedIds.has(k)) {
      setSavedIds(prev => new Set([...prev, k]));
    }
    try {
      await api.post(`/watchlist/${mt}/${id}/loved`, {
        title: item.title || item.name || "",
        poster_path: item.poster_path || null,
      });
    } catch {
      setLovedIds(prev => {
        const next = new Set(prev);
        nowLoved ? next.delete(k) : next.add(k);
        return next;
      });
    }
  }, [lovedIds, savedIds]);

  const saveToWatchlist = useCallback(async (mt, id, item = {}, listId = "later") => {
    const k = key(mt, id);
    setSavedIds(prev => new Set([...prev, k]));
    try {
      await api.post("/watchlist", {
        tmdb_id: id,
        media_type: mt,
        title: item.title || item.name || "",
        poster_path: item.poster_path || null,
        list_id: listId,
      });
    } catch {
      setSavedIds(prev => {
        const next = new Set(prev);
        next.delete(k);
        return next;
      });
    }
  }, []);

  // saveToList: saves to a specific list (add new or move existing item)
  const saveToList = useCallback(async (mt, id, item = {}, listId) => {
    const k = key(mt, id);
    setSavedIds(prev => new Set([...prev, k]));
    try {
      await api.post(`/watchlist/${mt}/${id}/list/${listId}`, {
        title: item.title || item.name || "",
        poster_path: item.poster_path || null,
      });
    } catch {
      setSavedIds(prev => { const n = new Set(prev); n.delete(k); return n; });
    }
  }, []);

  const removeFromWatchlist = useCallback(async (mt, id) => {
    const k = key(mt, id);
    setSavedIds(prev => { const n = new Set(prev); n.delete(k); return n; });
    setWatchedIds(prev => { const n = new Set(prev); n.delete(k); return n; });
    setLovedIds(prev => { const n = new Set(prev); n.delete(k); return n; });
    try {
      await api.delete(`/watchlist/${mt}/${id}`);
    } catch { load(); }
  }, [load]);

  return (
    <WatchStatusContext.Provider value={{
      watchedIds, lovedIds, savedIds, lists,
      isWatched, isLoved, isSaved,
      toggleWatched, toggleLoved, saveToWatchlist, saveToList, removeFromWatchlist,
      reload: load,
      addList: (list) => setLists(prev => [...prev, list]),
    }}>
      {children}
    </WatchStatusContext.Provider>
  );
}

export function useWatchStatus() {
  return useContext(WatchStatusContext) || {
    isWatched: () => false,
    isLoved: () => false,
    isSaved: () => false,
    toggleWatched: async () => {},
    toggleLoved: async () => {},
    saveToWatchlist: async () => {},
    saveToList: async () => {},
    removeFromWatchlist: async () => {},
    lists: [],
    reload: () => {},
    addList: () => {},
  };
}
